import {
  ACTION_ARGS,
  Arg,
  BUTTON_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  INPUT_MENU_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { Animated } from '../../core/capability/animated.gjs';
import { BUTTON_SIZES, NORMAL } from '../../../../utils/spds/constants/sizes';
import { compose } from '../../../../utils/spds/capabilities/helpers';
import { deactivate } from '../../../../utils/keyboard-nav-support';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import FlexColumn from '../../layout/flex/column.gjs';
import Menu from './dropdown/menu.gjs';
import Super from '../../core/keyboard-navigable.gjs';
import Trigger from './dropdown/trigger.gjs';

const METADATA = Metadata.define({
  description:
    'Button with dropdown menu that reveals additional options or actions. Built on `Spds::Core::KeyboardNavigable`: ArrowUp/ArrowDown open the menu and move through the items, Enter activates the focused item, Escape closes, and Tab leaves the component.',
  extends: [
    pick(ACTION_ARGS, ['onClose', 'onOpen']),
    pick(BUTTON_ARGS, ['buttonType', { name: 'label', initialValue: 'Button' }]),
    pick(COMPONENT_STATE_ARGS, ['autofocus', 'disabled']),
    pick(CONTAINER_ARGS, [
      { name: 'fitContent', default: true },
      'fitContentHeight',
      'fitContentWidth',
      'inline',
    ]),
    pick(MENU_ARGS, [
      'menuAnchorOffset',
      'menuItems',
      'menuMaxHeight',
      'menuMaxWidth',
      'menuMinWidth',
      'menuPlacement',
      'menuWidth',
      'onItemClick',
    ]),
    pick(INPUT_MENU_ARGS, ['noResultsText', 'searchable', 'searchPlaceholder', 'searchThreshold']),
  ],
  meta: {
    size: Arg.string({ default: NORMAL, description: 'Button size', options: BUTTON_SIZES }),
  },
  name: 'Dropdown Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=26311-17009&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/87c0a6-dropdown-button',
  },
});

export default class SpdsActionButtonDropdown extends compose(Super, Animated) {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-button spds-action-button';
  variantClass = 'spds-action-button-dropdown';

  wrapAround = true;

  get fitContent() {
    return this.argOrDefault('fitContent');
  }

  get menuCanOpen() {
    return this.navigableOptions.length > 0;
  }

  get menuId() {
    return this.composeSubcomponentId('menu');
  }

  get trigger() {
    return this.triggerComponent?.baseElement;
  }

  get triggerId() {
    return this.composeSubcomponentId('trigger');
  }

  exitToTrigger() {
    this.activeOption = undefined;
    this.trigger?.focus();
  }

  handleOpen = () => {
    this.open = true;
    this.args.onOpen?.();
  };

  handleClose = () => {
    this.open = false;
    // filtered-out options can still be carrying `active`, so clear the unfiltered list
    this.registeredOptions.forEach(deactivate);
    this.args.onClose?.();
  };

  toggleOpenState = () => {
    this.open ? this.handleClose() : this.handleOpen();
  };

  <template>
    <FlexColumn
      @id={{this.id}}
      class="spds-action-button-dropdown-container"
      data-test-spds-action-button-dropdown
      {{this.setupComponent}}
      {{this.validateArguments}}
    >
      <Trigger
        @autofocus={{@autofocus}}
        @buttonType={{@buttonType}}
        @disabled={{@disabled}}
        @fitContent={{this.fitContent}}
        @fitContentHeight={{@fitContentHeight}}
        @fitContentWidth={{@fitContentWidth}}
        @id={{this.triggerId}}
        @inline={{@inline}}
        @open={{this.open}}
        @registerComponent={{fn this.registerComponent "triggerComponent"}}
        @size={{@size}}
        @unregisterComponent={{fn this.unregisterComponent "triggerComponent"}}
        aria-controls={{this.menuId}}
        class={{this.classes}}
        {{on "click" this.toggleOpenState}}
        ...attributes
      >
        {{yield to="trigger"}}
        {{@label}}
      </Trigger>
      <Menu
        @anchorElement={{this.trigger}}
        @anchorOffset={{@menuAnchorOffset}}
        @animated={{this.animated}}
        @ariaLabelledBy={{this.triggerId}}
        @id={{this.menuId}}
        @maxHeight={{@menuMaxHeight}}
        @maxWidth={{@menuMaxWidth}}
        @minWidth={{@menuMinWidth}}
        @noResultsText={{@noResultsText}}
        @open={{this.open}}
        @placement={{@menuPlacement}}
        @registerComponent={{fn this.registerComponent "optionSource"}}
        @searchable={{@searchable}}
        @searchPlaceholder={{@searchPlaceholder}}
        @searchThreshold={{@searchThreshold}}
        @toggleOpenState={{this.toggleOpenState}}
        @unregisterComponent={{fn this.unregisterComponent "optionSource"}}
        @width={{@menuWidth}}
        as |menu|
      >
        {{#if (has-block "menu")}}
          {{yield (hash Item=(component menu.Item onClick=@onItemClick)) to="menu"}}
        {{else if @menuItems}}
          {{#each @menuItems as |item|}}
            <menu.Item
              @onClick={{or item.onClick @onItemClick}}
              @value={{or item.value item.label}}
            >
              {{item.label}}
            </menu.Item>
          {{/each}}
        {{/if}}
      </Menu>
    </FlexColumn>
  </template>
}

export const ACTION_BUTTON_DROPDOWN_METADATA = SpdsActionButtonDropdown.metadata;
